import { WhereClause } from '@osdk/client';

export const GLOBAL_KEYS_TO_SKIP = [
    'memid',
    'key',
    'orderBy'
];

export function transformQueryParams(queryParams: Record<string, unknown> | undefined): WhereClause<any> {
    const transformedParams: WhereClause<any> = {};
    const keysToSkip = ['key', 'hospitalId'];

    if (!queryParams) {
        return transformedParams;
    }

    // Helper to map UI operator to OSDK where-clause
    const mapToWhere = (field: string, operator: string, val: any): Record<string, any> => {
        switch (operator) {
            case 'equals':
                return { [field]: { $eq: val } };
            case 'notEquals':
                return { [field]: { $ne: val } };
            case 'greaterThan':
                return { [field]: { $gt: val } };
            case 'lessThan':
                return { [field]: { $lt: val } };
            case 'greaterThanEqual':
                return { [field]: { $gte: val } };
            case 'lessThanEqual':
                return { [field]: { $lte: val } };
            case 'contains':
                return { [field]: { $contains: val } };
            case 'doesNotContain':
                return { [field]: { $not: { $contains: val } } };
            case 'in': {
                const arr = Array.isArray(val)
                    ? val
                    : typeof val === 'string'
                        ? val.split(/[,\s]+/).filter(Boolean)
                        : [val];
                return { [field]: { $in: arr } };
            }
            case 'notIn': {
                const arr = Array.isArray(val)
                    ? val
                    : typeof val === 'string'
                        ? val.split(/[,\s]+/).filter(Boolean)
                        : [val];
                return { [field]: { $not: { $in: arr } } };
            }
            case 'isEmpty':
                return { [field]: { $eq: [] } };
            case 'isNotEmpty':
                return { [field]: { $ne: [] } };
            case 'isNull':
                return { [field]: { $isNull: true } };
            case 'isNotNull':
                return { [field]: { $not: { $isNull: true } } };
            // Text search helpers
            case 'startsWith':
                return { [field]: { $startsWith: val } } as any; // if unsupported, server should ignore
            default:
                return { [field]: { $eq: val } };
        }
    };

    // Support queryBuilder compound conditions
    const qb = (queryParams as any)['queryBuilder'];
    if (Array.isArray(qb) && qb.length > 0) {
        const clauses = qb
            .filter((c: any) => c && c.fieldName && c.operator)
            .map((c: any) => mapToWhere(c.fieldName, c.operator, c.value));
        // Combine with $and at top-level
        transformedParams['$and'] = clauses as any;
    }

    for (const [key, value] of Object.entries(queryParams)) {
        if (keysToSkip.includes(key)) continue;
        if (key === 'queryBuilder') continue;
        if (value === null || value === undefined || value === 'null') continue;
        if (typeof value === 'boolean' || value === 'false') continue;

        if (key === 'expiringInDays') {
            transformedParams[key] = { $lte: typeof value === 'string' ? Number(value) : (value as number) };
            continue;
        }
        if (Array.isArray(value)) {
            transformedParams[key] = { $in: value };
            continue;
        }
        transformedParams[key] = { $eq: value as string | number | boolean };
    }

    delete (transformedParams as any)['consignedOrLoaned'];

    return transformedParams;
}

export function toUrlParams(values: { [key: string]: string | number | boolean | null | undefined }): string {
    const params = new URLSearchParams();

    for (const [key, value] of Object.entries(values)) {
        if (value !== undefined && value !== null) {
            params.append(key, String(value));
        }
    }

    return params.toString();
}

export function cleanUnusedProperties<T extends Record<string, any>>(obj: T): Partial<T> {
    const cleanedObj: Partial<T> = {};

    for (const [key, value] of Object.entries(obj)) {
        if (value !== undefined && value !== null && value !== '' && value !== 'false') {
            (cleanedObj as Record<string, any>)[key] = value;
        }
    }

    return cleanedObj;
}

export const removeKeys = (keys: string[], obj: Record<string, any>) => {
    const newObj: Record<string, any> = {};
    for (const [key, value] of Object.entries(obj)) {
        if (![...keys, ...GLOBAL_KEYS_TO_SKIP].includes(key)) {
            newObj[key] = value;
        }
    }
    return newObj;
};

export const sortByColumn = (
    formValues: Record<string, any>
): { fields: Array<{ field: string; direction: string }> } | undefined => {
    const orderByValue = formValues.orderBy;
    if (orderByValue !== undefined && orderByValue !== null && orderByValue !== '') {
        return {
            fields: [
                {
                    field: orderByValue,
                    direction: 'asc'
                }
            ]
        };
    }
    return undefined;
};

export const formValuesToFoundryParams = (formValues: Record<string, any>, objectType: string): Record<string, any> => {
    // List of known number fields (extend as needed)
    const numberFields = [
        'parLevel',
        'lotDetails.qtyOnHand',
        'quantityLastYear',
        'expiringBucket',
        'currentPriceEach',
    ];
    const keysToSkip = [...GLOBAL_KEYS_TO_SKIP];
    const params: any[] = [];

    const mapOperatorToType = (op: string): string => {
        switch (op) {
            case 'equals':
                return 'eq';
            case 'notEquals':
                // We invert as NOT of EQ later
                return 'eq';
            case 'greaterThan':
                return 'gt';
            case 'lessThan':
                return 'lt';
            case 'greaterThanEqual':
                return 'gte';
            case 'lessThanEqual':
                return 'lte';
            case 'contains':
                return 'contains';
            case 'doesNotContain':
                // Invert as NOT of CONTAINS later
                return 'contains';
            case 'startsWith':
                return 'startsWith';
            case 'endsWith':
                // Fallback to contains when endsWith is not supported in Foundry params
                return 'contains';
            case 'in':
                return 'in';
            case 'notIn':
                // Invert as NOT of IN later
                return 'in';
            case 'isEmpty':
                // Represent as eq []
                return 'eq';
            case 'isNotEmpty':
                // Represent as NOT of eq [] later
                return 'eq';
            case 'isNull':
                return 'isNull';
            case 'isNotNull':
                // Represent as isNull false later
                return 'isNull';
            case 'containsAnyTerm':
            case 'containsAllTerms':
            case 'containsAllTermsInOrder':
                return op;
            default:
                return 'eq';
        }
    };

    const normalizeListValue = (val: any): any[] => {
        if (Array.isArray(val)) return val;
        if (typeof val === 'string') return val.split(/[,\s]+/).filter(Boolean);
        return [val];
    };

    const isUsageTransferFamily = (t: string) => t === 'UsageTransfer' || t === 'QsightPrdlegUsageTransfer';

    for (const [key, value] of Object.entries(formValues)) {
        if (keysToSkip.includes(key)) continue;

        if (['departmentId', 'hospitalId'].includes(key)) {
            const arr = Array.isArray(value) ? value : normalizeListValue(value);
            params.push({
                type: 'containsAnyTerm',
                field: key,
                value: arr.join(' '),
            });
            continue;
        }

        if (key === 'queryBuilder' && Array.isArray(value)) {
            for (const condition of value) {
                const noValueOpsLocal = ['isEmpty', 'isNotEmpty', 'isNull', 'isNotNull'];
                if (condition.fieldName && condition.operator && (condition.value !== undefined || noValueOpsLocal.includes(condition.operator))) {
                    const type = mapOperatorToType(condition.operator);
                    let outValue = condition.value;

                    // Convert to number if field is a known number field
                    if (numberFields.includes(condition.fieldName) && typeof outValue === 'string' && outValue !== '') {
                        const num = Number(outValue);
                        if (!isNaN(num)) outValue = num;
                    }

                    // Normalize values for list operators (notIn maps to in and is inverted below)
                    if (type === 'in') {
                        outValue = normalizeListValue(outValue).map(v =>
                            numberFields.includes(condition.fieldName) && typeof v === 'string' && v !== '' && !isNaN(Number(v))
                                ? Number(v)
                                : v
                        );
                    }
                    if (condition.operator === 'isNull') {
                        // Foundry expects boolean value for IsNullQueryV2
                        params.push({ type: 'isNull', field: condition.fieldName, value: true });
                        continue;
                    }
                    // Handle empty / not empty using eq [] with inversion
                    if (condition.operator === 'isEmpty') {
                        params.push({
                            type: 'eq',
                            field: condition.fieldName,
                            value: [],
                        });
                        continue;
                    }
                    if (condition.operator === 'isNotEmpty') {
                        params.push({
                            type: 'not',
                            value: [
                                {
                                    type: 'eq',
                                    field: condition.fieldName,
                                    value: [],
                                },
                            ],
                        });
                        continue;
                    }
                    if (condition.operator === 'isNotNull') {
                        // Represent NOT of isNull with value:true
                        params.push({
                            type: 'not',
                            value: [
                                {
                                    type: 'isNull',
                                    field: condition.fieldName,
                                    value: true,
                                },
                            ],
                        });
                        continue;
                    }

                    // Implement NOT filter for notEquals using inversion of eq
                    if (condition.operator === 'notEquals') {
                        params.push({
                            type: 'not',
                            value: [
                                {
                                    type: 'eq',
                                    field: condition.fieldName,
                                    value: outValue,
                                },
                            ],
                        });
                        continue;
                    }

                    // Implement NOT filter for doesNotContain using inversion of contains
                    if (condition.operator === 'doesNotContain') {
                        params.push({
                            type: 'not',
                            value: [
                                {
                                    type: 'contains',
                                    field: condition.fieldName,
                                    value: outValue,
                                },
                            ],
                        });
                        continue;
                    }

                    // Implement NOT filter for notIn using inversion of in
                    if (condition.operator === 'notIn') {
                        params.push({
                            type: 'not',
                            value: [
                                {
                                    type: 'in',
                                    field: condition.fieldName,
                                    value: outValue,
                                },
                            ],
                        });
                        continue;
                    }

                    params.push({
                        type,
                        field: condition.fieldName,
                        value: outValue,
                    });
                }
            }
            continue;
        }

        let outValue = value;
        if (numberFields.includes(key) && typeof outValue === 'string' && outValue !== '') {
            const num = Number(outValue);
            if (!isNaN(num)) outValue = num;
        }
        if (
            outValue !== undefined &&
            outValue !== null &&
            outValue !== '' &&
            outValue !== false &&
            outValue !== 'false' &&
            !(isUsageTransferFamily(objectType) && key === 'consigned')
        ) {
            params.push({
                type: 'eq',
                field: key,
                value: outValue,
            });
        }
    }

    return params;
};


export const sampleFormValues = {
    'queryBuilder': [
        {
            'fieldName': 'lotDetails.qtyOnHand',
            'operator': 'lessThan',
            'value': '4'
        },
        {
            'fieldName': 'catalogNo',
            'operator': 'contains',
            'value': 'asdf'
        },
        {
            'fieldName': 'productBrandName',
            'operator': 'equals',
            'value': 'asdf'
        }
    ],
    'hospitalId': false,
    'holderId': false,
    'expiringInDays': '30',
    'consigned': null
};